import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useMemo } from 'react';

type Language = 'sk' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, defaultValue?: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Default language if none is set
const DEFAULT_LANGUAGE: Language = 'sk';

// Type-safe translations
const translations: Record<Language, Record<string, string>> = {
  sk: {
    // Header
    'nav.home': 'Domov',
    'nav.about': 'O nás',
    'nav.activities': 'Naše aktivity',
    'nav.leadership': 'Naše vedenie',
    'nav.quality': 'Kvalita',
    'nav.certifications': 'Certifikácie',
    'nav.careers': 'Kariéra',
    'nav.contact': 'Kontakt',
    
    // Hero
    'hero.tagline': 'Skvelí ľudia, skvelá spoločnosť',
    'hero.description': 'IAC Group (Slovakia) sme založili z čistej vášne pre ľudí. Naše ciele, ambície a inšpirácie sa spojili a vytvorili úžasnú spoločnosť, na ktorú sme všetci hrdí.',
    'hero.learnMore': 'Viac o nás',
    'hero.contactUs': 'Kontaktujte nás',

    // Acquisition banner
    'acquisition.label': 'Dôležité oznámenie',
    'acquisition.title': 'IAC Group (Slovakia) bola oficiálne odkúpená spoločnosťou Artifex Systems Slovakia',
    'acquisition.description': 'Začíname novú etapu pod značkou Artifex Systems Slovakia. Pre najnovšie informácie, služby a firemné novinky navštívte našu novú oficiálnu webstránku.',
    'acquisition.cta': 'Prejsť na novú webstránku',
    'acquisition.blockingNote': 'Táto stránka je nahradená novým oficiálnym webom Artifex Systems Slovakia.',
    
    // About
    'about.badge': 'O IAC Group Slovakia',
    'about.title': 'Od roku 2000 sme tu pre vás',
    'about.description': 'IAC Group (Slovakia) s.r.o. je popredný dodávateľ pre automobilový priemysel, ktorý sa zaväzuje poskytovať inovatívne riešenia a výnimočnú kvalitu. Naša cesta začala jednoduchou víziou: vybudovať spoločnosť, kde skvelí ľudia môžu robiť skvelú prácu.',
    'about.values.people.title': 'Ľudia na prvom mieste',
    'about.values.people.description': 'Naša vášeň pre ľudí je hnacou silou všetkého, čo robíme. Veríme, že skvelí ľudia vytvárajú skvelé spoločnosti.',
    'about.values.excellence.title': 'Dokonalosť',
    'about.values.excellence.description': 'Od roku 2000 sme sa zaviazali poskytovať výnimočnú kvalitu a služby našim zákazníkom.',
    'about.values.quality.title': 'Štandardy kvality',
    'about.values.quality.description': 'Udržiavame najvyššie priemyselné štandardy s certifikovanými systémami riadenia kvality.',
    'about.values.innovation.title': 'Inovácie',
    'about.values.innovation.description': 'Neustále zlepšovanie a inovácie nás udržiavajú v popredí nášho odvetvia.',
    'about.commitment.title': 'Naše najmodernejšie zariadenie na Slovensku predstavuje náš záväzok k operačnej dokonalosti',
    'about.commitment.p1': 'Viac ako dve desaťročia je IAC Group (Slovakia) v popredí automobilových inovácií a poskytuje produkty a služby svetovej triedy našim globálnym partnerom.',
    'about.commitment.p2': 'Naše najmodernejšie zariadenie na Slovensku predstavuje náš záväzok k operačnej dokonalosti, spájajúc pokročilé technológie s kvalifikovaným remeselníckym umením na splnenie najvyšších priemyselných štandardov.',
    'about.commitment.p3': 'Sme hrdí na to, že sme súčasťou globálnej rodiny IAC Group a prispievame k budúcnosti mobility pri zachovaní našich základných hodnôt integrity, inovácií a spokojnosti zákazníkov.',
    'about.years': 'Rokov dokonalosti',
    
    // Services
    'services.badge': 'Naše služby',
    'services.title': 'Komplexné riešenia',
    'services.description': 'Poskytujeme komplexné automobilové riešenia, od výroby až po zákaznícku podporu, zabezpečujúc dokonalosť v každom kroku procesu.',
    'services.manufacturing.title': 'Automobilová výroba',
    'services.manufacturing.description': 'Pokročilé výrobné riešenia pre automobilové komponenty s presným inžinierstvom a zabezpečením kvality.',
    'services.manufacturing.feature1': 'Presné inžinierstvo',
    'services.manufacturing.feature2': 'Kontrola kvality',
    'services.manufacturing.feature3': 'Pokročilé materiály',
    'services.manufacturing.feature4': 'Optimalizácia procesov',
    'services.quality.title': 'Zabezpečenie kvality',
    'services.quality.description': 'Komplexné systémy riadenia kvality zabezpečujúce najvyššie štandardy v každom produkte a procese.',
    'services.quality.feature1': 'ISO certifikácia',
    'services.quality.feature2': 'Testovanie a validácia',
    'services.quality.feature3': 'Kontinuálne monitorovanie',
    'services.quality.feature4': 'Riadenie súladu',
    'services.supply.title': 'Riešenia dodávateľského reťazca',
    'services.supply.description': 'Efektívna logistika a riadenie dodávateľského reťazca na zabezpečenie včasného dodania a optimálneho riadenia zásob.',
    'services.supply.feature1': 'Just-in-Time dodávky',
    'services.supply.feature2': 'Riadenie zásob',
    'services.supply.feature3': 'Optimalizácia logistiky',
    'services.supply.feature4': 'Globálna distribúcia',
    'services.support.title': 'Zákaznícka podpora',
    'services.support.description': 'Dedikovaná zákaznícka služba a technická podpora na zabezpečenie spokojnosti a dlhodobých partnerstiev.',
    'services.support.feature1': '24/7 podpora',
    'services.support.feature2': 'Technické poradenstvo',
    'services.support.feature3': 'Popredajný servis',
    'services.support.feature4': 'Rozvoj partnerstva',
    'services.partner.title': 'Pripravení na partnerstvo s nami?',
    'services.partner.description': 'Zistite, ako vám IAC Group (Slovakia) môže pomôcť dosiahnuť vaše obchodné ciele s našimi komplexnými automobilovými riešeniami a odborným tímom.',
    'services.partner.getInTouch': 'Kontaktujte nás',
    'services.partner.learnMore': 'Viac informácií',
    
    // Quality
    'quality.badge': 'Štandardy kvality',
    'quality.title': 'Certifikovaná dokonalosť',
    'quality.description': 'Náš záväzok ku kvalite je demonštrovaný prostredníctvom našich komplexných certifikačných programov a iniciatív neustáleho zlepšovania.',
    'quality.certifications.title': 'Popredné priemyselné certifikácie',
    'quality.iso9001.description': 'Systémy riadenia kvality',
    'quality.iso14001.description': 'Riadenie životného prostredia',
    'quality.iatf16949.description': 'Automobilové riadenie kvality',
    'quality.commitment.title': 'Náš záväzok ku kvalite',
    'quality.point1': 'Procesy neustáleho zlepšovania',
    'quality.point2': 'Pokročilé systémy kontroly kvality',
    'quality.point3': 'Certifikované testovacie postupy',
    'quality.point4': 'Zameranie na spokojnosť zákazníkov',
    'quality.point5': 'Environmentálna zodpovednosť',
    'quality.point6': 'Školenie a rozvoj zamestnancov',

    // Leadership
    'leadership.title': 'Naše vedenie',
    'leadership.description': 'Spoznajte náš tím skúsených lídrov, ktorí riadia spoločnosť IAC Group Slovakia s dôrazom na inovácie, kvalitu a udržateľný rast.',
    'leadership.structure.title': 'Organizačná štruktúra',
    'leadership.executive.title': 'Výkonné vedenie',
    'leadership.executive.description': 'Generálne a funkčné riaditeľstvo',
    'leadership.senior.title': 'Senior manažment',
    'leadership.senior.description': 'Riaditelia divízií a oddelení',
    'leadership.middle.title': 'Stredný manažment',
    'leadership.middle.description': 'Vedúci tímov a projektov',
    'leadership.team.title': 'Vedúci pracovníci',
    'leadership.cta.title': 'Chcete sa stať súčasťou nášho tímu?',
    'leadership.cta.description': 'Hľadáme talentovaných ľudí, ktorí sa chcú pridať k nášmu úspešnému tímu. Pozrite sa na naše aktuálne pracovné ponuky.',
    'leadership.cta.button': 'Pozrieť voľné pozície',
    
    // Contact
    'contact.badge': 'Kontaktujte nás',
    'contact.title': 'Spojte sa s nami',
    'contact.description': 'Pripravení začať partnerstvo? Máte otázky o našich službách? Radi sa od vás počujeme. Kontaktujte nás ešte dnes!',
    'contact.address': 'Adresa',
    'contact.phone': 'Telefón',
    'contact.email': 'Email',
    'contact.hours': 'Pracovné hodiny',
    'contact.hours.weekdays': 'Po - Pi: 8:00 - 17:00',
    'contact.hours.weekend': 'So - Ne: Zatvorené',
    'contact.form.title': 'Pošlite nám správu',
    'contact.form.name': 'Meno',
    'contact.form.email': 'Email',
    'contact.form.company': 'Spoločnosť',
    'contact.form.message': 'Správa',
    'contact.form.placeholder': 'Napíšte nám o vašom projekte alebo požiadavke...',
    'contact.form.send': 'Odoslať správu',
    'contact.form.success.title': 'Správa odoslaná!',
    'contact.form.success.description': 'Ďakujeme za váš dopyt. Čoskoro sa vám ozveme.',
    
    // Footer
    'footer.description': 'Skvelí ľudia, skvelá spoločnosť. Slúžime automobilovému priemyslu s dokonalosťou od roku 2000.',
    'footer.quickLinks': 'Rýchle odkazy',
    'footer.contact': 'Kontakt',
    'footer.copyright': '© 2024 IAC Group (Slovakia) s.r.o. Všetky práva vyhradené.',
    'footer.privacy': 'Ochrana súkromia',
    'footer.terms': 'Podmienky služby',
    'footer.admin': 'Admin Panel',
    
    // Careers - Empty state
    'careers.empty.title': 'Momentálne sme plne pokrytí! 🎉',
    'careers.empty.description': 'Vypadá to, že všetky naše pozície sú obsadené skvelými kolegami. Ale nebojte sa - nové príležitosti sa určite objavia!',
    'careers.empty.subscribe': 'Chcete vedieť ako prvý?',
    'careers.empty.email.placeholder': 'Váš email',
    'careers.empty.submit': 'Odoslať',
    'careers.empty.success': 'Úspešne ste sa prihlásili k odberu! 📧',
    'careers.empty.success.description': 'Keď sa objaví nová pozícia, dáme vám vedieť.',
    'careers.empty.already.subscribed': 'Už ste prihlásený k odberu!',
    'careers.empty.fun.fact1': 'Medzitým: Vedeli ste, že náš najdlhšie pracujúci kolega je s nami už 23 rokov?',
    'careers.empty.fun.fact2': 'Alebo že máme vlastnú závodnú jedáleň s výborným gulášom?',
    'careers.empty.fun.fact3': 'A čo takto: Naše parkovisko má 150 miest - to je skoro ako malé mestečko!',
  },
  en: {
    // Header
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.activities': 'Our Activities',
    'nav.leadership': 'Our Leadership',
    'nav.quality': 'Quality',
    'nav.certifications': 'Certifications',
    'nav.careers': 'Careers',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.tagline': 'Great People, Great Company',
    'hero.description': 'We founded IAC Group (Slovakia) out of a pure passion for people. Our goals, ambitions and inspirations have come together to form a truly amazing company that all of us are proud to be a part of.',
    'hero.learnMore': 'Learn More About Us',
    'hero.contactUs': 'Contact Us',

    // Acquisition banner
    'acquisition.label': 'Important announcement',
    'acquisition.title': 'IAC Group (Slovakia) has been officially acquired by Artifex Systems Slovakia',
    'acquisition.description': 'We are entering a new chapter under the Artifex Systems Slovakia brand. For the latest company updates, services, and information, please visit our new official website.',
    'acquisition.cta': 'Visit the new website',
    'acquisition.blockingNote': 'This page has been replaced by the new official Artifex Systems Slovakia website.',
    
    // About
    'about.badge': 'About IAC Group Slovakia',
    'about.title': 'Serving Excellence Since 2000',
    'about.description': 'IAC Group (Slovakia) s.r.o. is a leading automotive supplier committed to delivering innovative solutions and exceptional quality. Our journey began with a simple vision: to build a company where great people can do great work.',
    'about.values.people.title': 'People First',
    'about.values.people.description': 'Our passion for people drives everything we do. We believe that great people create great companies.',
    'about.values.excellence.title': 'Excellence',
    'about.values.excellence.description': 'Since 2000, we\'ve been committed to delivering exceptional quality and service to our customers.',
    'about.values.quality.title': 'Quality Standards',
    'about.values.quality.description': 'We maintain the highest industry standards with certified quality management systems.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description': 'Continuous improvement and innovation keep us at the forefront of our industry.',
    'about.commitment.title': 'Our Commitment to Excellence',
    'about.commitment.p1': 'For over two decades, IAC Group (Slovakia) has been at the forefront of automotive innovation, delivering world-class products and services to our global partners.',
    'about.commitment.p2': 'Our state-of-the-art facility in Slovakia represents our commitment to operational excellence, combining advanced technology with skilled craftsmanship to meet the highest industry standards.',
    'about.commitment.p3': 'We are proud to be part of the global IAC Group family, contributing to the future of mobility while maintaining our core values of integrity, innovation, and customer satisfaction.',
    'about.years': 'Years of Excellence',

    // Services
    'services.badge': 'Our Services',
    'services.title': 'Comprehensive Solutions',
    'services.description': 'We provide end-to-end automotive solutions, from manufacturing to customer support, ensuring excellence at every step of the process.',
    'services.manufacturing.title': 'Automotive Manufacturing',
    'services.manufacturing.description': 'Advanced manufacturing solutions for automotive components with precision engineering and quality assurance.',
    'services.manufacturing.feature1': 'Precision Engineering',
    'services.manufacturing.feature2': 'Quality Control',
    'services.manufacturing.feature3': 'Advanced Materials',
    'services.manufacturing.feature4': 'Process Optimization',
    'services.quality.title': 'Quality Assurance',
    'services.quality.description': 'Comprehensive quality management systems ensuring the highest standards in every product and process.',
    'services.quality.feature1': 'ISO Certification',
    'services.quality.feature2': 'Testing & Validation',
    'services.quality.feature3': 'Continuous Monitoring',
    'services.quality.feature4': 'Compliance Management',
    'services.supply.title': 'Supply Chain Solutions',
    'services.supply.description': 'Efficient logistics and supply chain management to ensure timely delivery and optimal inventory management.',
    'services.supply.feature1': 'Just-in-Time Delivery',
    'services.supply.feature2': 'Inventory Management',
    'services.supply.feature3': 'Logistics Optimization',
    'services.supply.feature4': 'Global Distribution',
    'services.support.title': 'Customer Support',
    'services.support.description': 'Dedicated customer service and technical support to ensure satisfaction and long-term partnerships.',
    'services.support.feature1': '24/7 Support',
    'services.support.feature2': 'Technical Consultation',
    'services.support.feature3': 'After-Sales Service',
    'services.support.feature4': 'Partnership Development',
    'services.partner.title': 'Ready to Partner with Us?',
    'services.partner.description': 'Discover how IAC Group (Slovakia) can help your business achieve its goals with our comprehensive automotive solutions and expert team.',
    'services.partner.getInTouch': 'Get in Touch',
    'services.partner.learnMore': 'Learn More',
    
    // Quality
    'quality.badge': 'Quality Standards',
    'quality.title': 'Certified Excellence',
    'quality.description': 'Our commitment to quality is demonstrated through our comprehensive certification programs and continuous improvement initiatives.',
    'quality.certifications.title': 'Industry-Leading Certifications',
    'quality.iso9001.description': 'Quality Management Systems',
    'quality.iso14001.description': 'Environmental Management',
    'quality.iatf16949.description': 'Automotive Quality Management',
    'quality.commitment.title': 'Our Quality Commitment',
    'quality.point1': 'Continuous improvement processes',
    'quality.point2': 'Advanced quality control systems',
    'quality.point3': 'Certified testing procedures',
    'quality.point4': 'Customer satisfaction focus',
    'quality.point5': 'Environmental responsibility',
    'quality.point6': 'Employee training and development',
    
    // Leadership
    'leadership.title': 'Our Leadership',
    'leadership.description': 'Meet our team of experienced leaders who drive IAC Group Slovakia with a focus on innovation, quality, and sustainable growth.',
    'leadership.structure.title': 'Organizational Structure',
    'leadership.executive.title': 'Executive Leadership',
    'leadership.executive.description': 'General and functional management',
    'leadership.senior.title': 'Senior Management',
    'leadership.senior.description': 'Division and department directors',
    'leadership.middle.title': 'Middle Management',
    'leadership.middle.description': 'Team and project leaders',
    'leadership.team.title': 'Leadership Team',
    'leadership.cta.title': 'Want to join our team?',
    'leadership.cta.description': 'We are looking for talented people who want to join our successful team. Check out our current job openings.',
    'leadership.cta.button': 'View Open Positions',
    
    // Contact
    'contact.badge': 'Contact Us',
    'contact.title': 'Get in Touch',
    'contact.description': 'Ready to start a partnership? Have questions about our services? We\'d love to hear from you. Contact us today!',
    'contact.address': 'Address',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.hours': 'Business Hours',
    'contact.hours.weekdays': 'Mon - Fri: 8:00 - 17:00',
    'contact.hours.weekend': 'Sat - Sun: Closed',
    'contact.form.title': 'Send us a Message',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.company': 'Company',
    'contact.form.message': 'Message',
    'contact.form.placeholder': 'Tell us about your project or inquiry...',
    'contact.form.send': 'Send Message',
    'contact.form.success.title': 'Message Sent!',
    'contact.form.success.description': 'Thank you for your inquiry. We\'ll get back to you soon.',
    
    // Footer
    'footer.description': 'Great People, Great Company. Serving the automotive industry with excellence since 2000.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2024 IAC Group (Slovakia) s.r.o. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.admin': 'Admin Panel',
    
    // Careers - Empty state
    'careers.empty.title': 'We\'re fully covered! 🎉',
    'careers.empty.description': 'It looks like all our positions are filled with amazing colleagues. But don\'t worry - new opportunities will definitely appear!',
    'careers.empty.subscribe': 'Want to be the first to know?',
    'careers.empty.email.placeholder': 'Your email',
    'careers.empty.submit': 'Submit',
    'careers.empty.success': 'Successfully subscribed! 📧',
    'careers.empty.success.description': 'When a new position appears, we\'ll let you know.',
    'careers.empty.already.subscribed': 'You are already subscribed!',
    'careers.empty.fun.fact1': 'Meanwhile: Did you know our longest-working colleague has been with us for 23 years?',
    'careers.empty.fun.fact2': 'Or that we have our own canteen with excellent goulash?',
    'careers.empty.fun.fact3': 'How about this: Our parking lot has 150 spaces - that\'s almost like a small town!',
  }
};

// Function to get the initial language
const getInitialLanguage = (): Language => {
  // Server-side rendering guard
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  try {
    // 1. Check URL first for language parameter
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang') as Language | null;
    
    if (langParam && (langParam === 'sk' || langParam === 'en')) {
      localStorage.setItem('language', langParam);
      return langParam;
    }

    // 2. Check localStorage for previously set language
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage && (savedLanguage === 'sk' || savedLanguage === 'en')) {
      return savedLanguage;
    }

    // 3. Fall back to browser language
    const browserLanguage = navigator.language.split('-')[0] as Language;
    if (browserLanguage === 'sk' || browserLanguage === 'en') {
      return browserLanguage;
    }
  } catch (error) {
    console.error('Error determining initial language:', error);
  }

  // 4. Ultimate fallback to default language
  return DEFAULT_LANGUAGE;
};

// Define the LanguageProvider props
interface LanguageProviderProps {
  children: ReactNode;
}

// Define the LanguageProvider component
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language on mount
  useEffect(() => {
    setLanguageState(getInitialLanguage());
    setIsInitialized(true);
  }, []);

  // Define the setLanguage function
  const setLanguage = useCallback((lang: Language) => {
    try {
      if (lang !== 'sk' && lang !== 'en') {
        console.warn(`Attempted to set invalid language: ${lang}. Defaulting to ${DEFAULT_LANGUAGE}`);
        lang = DEFAULT_LANGUAGE;
      }
      
      localStorage.setItem('language', lang);
      setLanguageState(lang);
      
      // Update URL without page reload
      const url = new URL(window.location.href);
      url.searchParams.set('lang', lang);
      window.history.pushState({}, '', url);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }, []);

  // Define the translation function
  const t = useCallback((key: string, defaultValue: string = ''): string => {
    if (!isInitialized) return defaultValue || key;
    
    try {
      // Try current language first
      const translation = translations[language]?.[key];
      if (translation) return translation;
      
      // Fall back to default language
      const defaultTranslation = translations[DEFAULT_LANGUAGE]?.[key];
      if (defaultTranslation) return defaultTranslation;
      
      // Log missing translations in development
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Missing translation for key: ${key}`);
      }
      
      return defaultValue || key;
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error);
      return defaultValue || key;
    }
  }, [language, isInitialized]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<LanguageContextType>(() => ({
    language,
    setLanguage,
    t,
  }), [language, setLanguage, t]);

  // Don't render children until language is initialized to prevent hydration mismatch
  if (!isInitialized) {
    return null;
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Define the useLanguage hook
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;